import React, { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import styled, { createGlobalStyle } from 'styled-components';
import { useScroll, useMotionValueEvent } from 'framer-motion';

const MarkerStyles = createGlobalStyle`
  .sofia-marker {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    cursor: default;
    will-change: opacity, transform;
    opacity: 0;
    transform: translateY(10px);
    transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .sofia-marker.visible {
    opacity: 1;
    transform: translateY(0);
  }
  .sofia-marker.hero {
    /* Sofia entra junto do primeiro frame visível, sempre stays */
  }

  /* Label hidden por padrão nos pins não-hero — só aparece quando a veia chega */
  .sofia-marker:not(.hero) .label {
    opacity: 0;
    transform: translateY(4px);
    transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
    pointer-events: none;
  }
  .sofia-marker:not(.hero).label-visible .label {
    opacity: 1;
    transform: translateY(0);
  }
  .sofia-marker.label-top {
    flex-direction: column-reverse;
    gap: 4px;
  }
  .sofia-marker.label-top .label {
    margin-bottom: 0;
  }
  /* Cursor pointer indica clicabilidade — só nos não-hero */
  .sofia-marker:not(.hero) {
    cursor: pointer;
  }
  .sofia-marker:not(.hero):hover .pin {
    box-shadow:
      0 0 8px rgba(255, 240, 210, 1),
      0 0 22px rgba(245, 220, 180, 1),
      0 0 48px rgba(220, 195, 160, 0.7);
  }
  .sofia-marker .pin {
    width: 11px;
    height: 11px;
    background: #f5d488;
    border-radius: 50%;
    box-shadow:
      0 0 6px rgba(255, 220, 130, 1),
      0 0 18px rgba(240, 200, 112, 0.95),
      0 0 36px rgba(212, 168, 71, 0.55);
  }
  .sofia-marker .label {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    line-height: 1.1;
    position: relative;
    isolation: isolate;
  }
  /* Halo radial sutil atrás do label não-hero pra leitura sobre o mapa */
  .sofia-marker:not(.hero) .label::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 180%;
    height: 240%;
    transform: translate(-50%, -50%);
    background: radial-gradient(
      ellipse closest-side,
      rgba(0,0,0,0.75) 0%,
      rgba(0,0,0,0.5) 35%,
      rgba(0,0,0,0.22) 65%,
      rgba(0,0,0,0) 100%
    );
    z-index: -1;
    pointer-events: none;
    filter: blur(3px);
  }
  .sofia-marker .label .name {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.18em;
    color: #f5e0b0;
    text-transform: uppercase;
    white-space: nowrap;
    text-shadow: 0 0 4px rgba(0,0,0,0.95), 0 0 8px rgba(0,0,0,0.85);
  }
  .sofia-marker .label .detail {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.2em;
    color: #d4a847;
    text-transform: uppercase;
    white-space: nowrap;
    text-shadow: 0 0 4px rgba(0,0,0,1), 0 0 8px rgba(0,0,0,0.85);
    margin-top: 1px;
  }
  /* Sofia hero: marca minimalista premium — apenas "Sofia" italic com
     hairline rule dourado em cima (vibe brand mark de hotel/residence luxo) */
  .sofia-marker.hero {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
  .sofia-hero-eyebrow {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 8px;
    font-weight: 500;
    letter-spacing: 0.5em;
    text-transform: uppercase;
    color: #e9c87a;
    opacity: 0.85;
    margin-right: -0.5em;  /* compensa o tracking do último char */
    text-shadow: 0 0 4px rgba(0,0,0,1), 0 0 8px rgba(0,0,0,1);

    @media (min-width: 768px) {
      font-size: 9px;
    }
  }
  .sofia-hero-name {
    font-family: 'Playfair Display', 'Cormorant Garamond', Georgia, serif;
    font-style: italic;
    font-weight: 400;
    font-size: 44px;
    letter-spacing: 0.02em;
    text-transform: uppercase;  /* matching o "SOFIA" do hero (caps Playfair italic) */
    color: #faf3e0;
    line-height: 1;
    position: relative;
    padding-top: 16px;
    text-shadow:
      0 0 6px rgba(0,0,0,1),
      0 0 14px rgba(0,0,0,1),
      0 0 28px rgba(0,0,0,1),
      0 0 50px rgba(0,0,0,0.95),
      0 0 90px rgba(0,0,0,0.85),
      0 0 140px rgba(0,0,0,0.6),
      0 4px 14px rgba(0,0,0,1);

    @media (min-width: 768px) {
      font-size: 60px;
      padding-top: 20px;
    }
  }
  /* Hairline rule dourado discreto acima do nome */
  .sofia-hero-name::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 36px;
    height: 1px;
    background: linear-gradient(90deg, transparent, #e9c87a 50%, transparent);
    opacity: 0.7;
    z-index: 1;
  }
  /* Halo radial atrás do SOFIA — escurece o mapa pra texto saltar */
  .sofia-hero-name::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 320%;
    height: 400%;
    transform: translate(-50%, -50%);
    background: radial-gradient(
      ellipse closest-side,
      rgba(0,0,0,1) 0%,
      rgba(0,0,0,0.92) 18%,
      rgba(0,0,0,0.75) 38%,
      rgba(0,0,0,0.5) 58%,
      rgba(0,0,0,0.25) 78%,
      rgba(0,0,0,0) 100%
    );
    z-index: -1;
    pointer-events: none;
    filter: blur(3px);

    @media (min-width: 768px) {
      width: 280%;
      height: 340%;
    }
  }
  .sofia-marker.hero .pin {
    width: 20px;
    height: 20px;
    background: #fff0c8;
    border-radius: 50%;
    box-shadow:
      0 0 8px rgba(255, 245, 200, 1),
      0 0 24px rgba(255, 220, 130, 1),
      0 0 56px rgba(240, 200, 112, 0.9),
      0 0 100px rgba(212, 168, 71, 0.5);
  }
`;

const SOFIA_COORDS = [-46.6635, -23.6168];

// Tempos oficiais do book Nortis (4 primeiros) + estimativas baseadas em distância
// geográfica + tráfego típico de Campo Belo/Brooklin/Morumbi (5 últimos).
// Refinar via Google Directions API quando tiver chave configurada.
const LANDMARKS = [
  { id: 'sofia',      label: 'SOFIA',                coords: [-46.6635, -23.6168], hero: true },
  { id: 'roberto',    label: 'AV. ROBERTO MARINHO', coords: [-46.6791, -23.6184], detail: '1 MIN' },
  { id: 'metro',      label: 'METRÔ CAMPO BELO',     coords: [-46.6743, -23.6262], detail: '2 MIN' },
  { id: 'aubrick',    label: 'COLÉGIO AUBRICK',      coords: [-46.6655, -23.6024], detail: '5 MIN' },
  { id: 'berrini',    label: 'AV. BERRINI',          coords: [-46.6926, -23.6093], detail: '7 MIN' },
  { id: 'ibirapuera', label: 'PARQUE IBIRAPUERA',    coords: [-46.6576, -23.5874], detail: '7 MIN' },
  { id: 'jk',         label: 'JK IGUATEMI',          coords: [-46.6864, -23.5957], detail: '8 MIN' },
  { id: 'sta-paula',  label: 'HOSPITAL SANTA PAULA', coords: [-46.6671, -23.5855], detail: '8 MIN', labelPosition: 'top' },
  { id: 'congonhas',  label: 'AEROPORTO CONGONHAS',  coords: [-46.6553, -23.6261], detail: '9 MIN' },
  { id: 'morumbi',    label: 'SHOPPING MORUMBI',     coords: [-46.6997, -23.6224], detail: '10 MIN' },
];

const Section = styled.section`
  position: relative;
  width: 100%;
  height: 80vh;
  background: #0a070b;
  overflow: hidden;

  @media (min-width: 768px) {
    height: 100vh;
  }

  /* Bridge com o Interlude (que termina em #0a070b puro):
     mascara o topo do mapa com preto sólido fadeando pra transparente.
     200px com easing curvado (mais black-heavy no topo, fade rápido no fim)
     pra eliminar a "linha" perceptível no boundary. */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(
      180deg,
      #0a070b 0%,
      rgba(10, 7, 11, 0.6) 50%,
      rgba(10, 7, 11, 0) 100%
    );
    pointer-events: none;
    z-index: 2;
  }
`;

const MapContainer = styled.div`
  /* Altura fixa 100vh ancorada no topo — Section tem overflow:hidden e
     pode ser menor que 100vh (ex.: 80vh no mobile), o que clipa o BOTTOM
     do mapa sem cortar o topo (onde estão SOFIA/CAMPO BELO). */
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100vh;

  /* esconde controles default do MapLibre */
  .maplibregl-ctrl,
  .maplibregl-ctrl-attrib { display: none !important; }
`;

const WarmOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
  background:
    /* sunset glow no centro — vibe quente onde está o Sofia */
    radial-gradient(ellipse 70% 50% at center,
      rgba(230, 140, 70, 0.22) 0%,
      rgba(200, 110, 55, 0.12) 30%,
      rgba(160, 80, 40, 0.05) 55%,
      rgba(10, 7, 11, 0) 75%
    ),
    /* tom warm geral sutil */
    linear-gradient(180deg,
      rgba(40, 22, 12, 0.18) 0%,
      rgba(40, 22, 12, 0.10) 50%,
      rgba(40, 22, 12, 0.18) 100%
    ),
    /* fade nas bordas — começa OPACO 1.0 (Interlude) e termina OPACO 1.0
       (encosta no #0a070b sólido do PlantaInterativa). Bottom fade curto
       (88% → 100%) pra não criar espaço preto gigante antes da próxima seção;
       a linha do boundary é mascarada pelo ::before do PlantaInterativa. */
    linear-gradient(180deg,
      rgba(10, 7, 11, 0.85) 0%,
      rgba(10, 7, 11, 0.35) 5%,
      rgba(10, 7, 11, 0.05) 12%,
      rgba(10, 7, 11, 0) 18%,
      rgba(10, 7, 11, 0) 88%,
      rgba(10, 7, 11, 0.6) 95%,
      rgba(10, 7, 11, 1) 100%
    );
`;

const SectionTitle = styled.div`
  position: absolute;
  top: 48px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  font-family: 'Playfair Display', 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-weight: 400;
  font-size: clamp(56px, 14vw, 110px);
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #faf3e0;
  white-space: nowrap;
  text-shadow:
    0 4px 24px rgba(0, 0, 0, 1),
    0 0 60px rgba(0, 0, 0, 0.85),
    0 0 120px rgba(0, 0, 0, 0.6);

  @media (min-width: 768px) {
    top: 72px;
  }
`;

export default function EntornoMapa() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const sectionRef = useRef(null);
  const markersRef = useRef([]);  // [{el, isHero}] na ordem do array LANDMARKS
  const routeAnimRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    mapRef.current = new maplibregl.Map({
      container: containerRef.current,
      style: 'https://tiles.openfreemap.org/styles/dark',
      center: SOFIA_COORDS,
      zoom: 12.4,
      pitch: 0,
      bearing: -8,
      attributionControl: false,
      interactive: false,
    });

    mapRef.current.on('load', () => {
      // Tenta deixar mais warm: troca cor de algumas layers conhecidas do "dark"
      const map = mapRef.current;
      const tweak = (id, paint) => {
        if (!map.getLayer(id)) return;
        Object.entries(paint).forEach(([k, v]) => {
          try { map.setPaintProperty(id, k, v); } catch (e) {}
        });
      };

      // Background recuado — bem escuro warm pra ruas saltarem por contraste
      tweak('background', { 'background-color': '#120c08' });
      // Ruas: cor bronze original + ESPESSURA aumentada (sem brilho extra, só presença)
      tweak('road_minor',   { 'line-color': '#7a5836', 'line-width': 1.4 });
      tweak('road_major',   { 'line-color': '#9a704a', 'line-width': 2.4 });
      tweak('road_motorway',{ 'line-color': '#c89058', 'line-width': 4.5 });
      tweak('road_trunk',   { 'line-color': '#b88450', 'line-width': 3.5 });

      // Labels em amber warm
      ['label_country','label_state','label_city','label_other','place_other','place_city','place_town','place_village'].forEach(id => {
        tweak(id, { 'text-color': '#e8c895', 'text-halo-color': '#150d08' });
      });

      // Water mais escuro ainda — recede tudo que não é rua
      tweak('water', { 'fill-color': '#0a0706' });
      // Parques e landuse bem subtle
      tweak('park',     { 'fill-color': '#1a1410' });
      tweak('landuse',  { 'fill-color': '#15100a' });

      // Source + layer da rota (vazio inicialmente — populado quando user clica num pin)
      // lineMetrics: true habilita ['line-progress'] pra animação de drawing
      map.addSource('route', {
        type: 'geojson',
        lineMetrics: true,
        data: { type: 'FeatureCollection', features: [] },
      });
      // Halo das rotas — atrás da linha principal, blur grande
      // line-gradient com line-progress funciona PER-FEATURE, então o mesmo
      // gradient anima TODAS as rotas em sincronia (cada uma do seu próprio start)
      const initialGradient = [
        'interpolate', ['linear'], ['line-progress'],
        0, 'rgba(250, 215, 140, 0)',
        1, 'rgba(250, 215, 140, 0)',
      ];
      map.addLayer({
        id: 'route-line-halo',
        type: 'line',
        source: 'route',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-width': 9,
          'line-opacity': 0.4,
          'line-blur': 4,
          'line-gradient': initialGradient,
        },
      });
      map.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-width': 3.5,
          'line-opacity': 1,
          'line-blur': 0.4,
          'line-gradient': initialGradient,
        },
      });

      // Adiciona markers (Sofia hero entra como visible já; resto fica oculto até scroll-reveal)
      LANDMARKS.forEach(({ id, label, coords, hero, detail, labelPosition }) => {
        const el = document.createElement('div');
        const classes = ['sofia-marker'];
        if (hero) classes.push('hero', 'visible');
        if (labelPosition === 'top') classes.push('label-top');
        el.className = classes.join(' ');

        if (hero) {
          // Sofia: marca minimalista — nome italic + bolinha
          el.innerHTML = `
            <div class="sofia-hero-name">Sofia</div>
            <div class="pin"></div>
          `;
        } else {
          const detailHtml = detail ? `<div class="detail">${detail}</div>` : '';
          el.innerHTML = `
            <div class="pin"></div>
            <div class="label">
              <div class="name">${label}</div>
              ${detailHtml}
            </div>
          `;
        }

        // Alinhamento do anchor pra coord geográfica:
        // - Hero (imagem): anchor 'bottom' → ponta do pin na coord (de onde as veias saem)
        // - Não-hero label-top: anchor 'bottom' + offset positivo (pin no topo do stack)
        // - Não-hero default: anchor 'top' + offset negativo (pin no topo do stack)
        let anchor, offset;
        if (hero) {
          // Hero stack: [pin image, bolinha]. Anchor 'bottom' = bottom edge na coord.
          // Offset [0, +10] empurra pra baixo, fazendo o CENTRO da bolinha (20px) coincidir com a coord.
          // Resultado: linhas saem exatamente do centro da bolinha (bottom da stack visual).
          anchor = 'bottom';
          offset = [0, 10];
        } else {
          const pinRadius = 5.5;  // pin é 11px
          const isLabelTop = labelPosition === 'top';
          anchor = isLabelTop ? 'bottom' : 'top';
          offset = isLabelTop ? [0, pinRadius] : [0, -pinRadius];
        }

        new maplibregl.Marker({ element: el, anchor, offset })
          .setLngLat(coords)
          .addTo(map);
        markersRef.current.push({ el, isHero: !!hero, id, coords });

        // (sem click handler — scroll dirige o desenho de TODAS as rotas em sincronia)
      });

      // Expor pra debug no console
      if (typeof window !== 'undefined') window.__sofiaMap = map;

      setMapReady(true);

      // Carrega as 9 rotas pré-cacheadas em JSON estático (public/sofia/routes.json).
      // Antes fetchávamos OSRM live a cada page load — frágil (rate-limit + servidor
      // gratuito caía periodicamente). Rotas são fixas (Sofia → mesmos 9 landmarks),
      // então JSON estático é instant + offline-first + zero dependência externa.
      // Pra regenerar (quando topologia mudar ou adicionar landmark), rodar:
      //   python3 -c "import json,urllib.request as u; S=[-46.6635,-23.6168]; L=$(\
      //     LANDMARKS abaixo); fc={'type':'FeatureCollection','features':[]}; \
      //     [fc['features'].append({'type':'Feature','id':i,'properties':{'id':i},\
      //     'geometry':{'type':'LineString','coordinates':[S]+json.loads(\
      //     u.urlopen(f'https://router.project-osrm.org/route/v1/driving/{S[0]},{S[1]};{t[0]},{t[1]}?overview=full&geometries=geojson').read())['routes'][0]['geometry']['coordinates']+[t]}}) for i,t in L]; \
      //     open('public/sofia/routes.json','w').write(json.dumps(fc,separators=(',',':')))"
      fetch('/sofia/routes.json')
        .then(r => r.json())
        .then(fc => {
          if (mapRef.current?.getSource('route') && fc?.features) {
            mapRef.current.getSource('route').setData(fc);
          }
        })
        .catch(() => {});
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Reveal scroll-progressivo:
  // 1) pins entram um por um (cascata)
  // 2) TODAS as rotas crescem em sincronia do Sofia pra fora ("veias do coração")
  useMotionValueEvent(scrollYProgress, 'change', (progress) => {
    if (!markersRef.current.length) return;
    const nonHero = markersRef.current.filter(m => !m.isHero);
    const N = nonHero.length;

    // Janela de reveal: começa quando Sofia label fica confortavelmente visível na tela.
    // Como a página acaba logo após o mapa, o scrollYProgress MÁX atingível é ~0.46
    // (e não 1.0) — então o range inteiro (veias + labels) precisa caber DENTRO disso.
    const startAt = 0.25;   // Sofia visível (section ~50% dentro do viewport)
    const endAt = 0.38;     // Veias completam (acelerado, "fim antes do scroll travar")
    const span = endAt - startAt;

    // Pins (dots) em cascata — sem labels ainda
    nonHero.forEach((m, idx) => {
      const threshold = startAt + (idx / Math.max(N - 1, 1)) * span;
      if (progress >= threshold) m.el.classList.add('visible');
      else m.el.classList.remove('visible');
    });

    // Labels: aparecem em cascata DEPOIS das veias completarem.
    // Range 0.38 → 0.44 — fica abaixo do máx 0.46, garantindo que todos
    // disparem antes do scroll bater no fundo da página.
    const labelStartAt = 0.38;
    const labelEndAt = 0.44;
    const labelSpan = labelEndAt - labelStartAt;
    nonHero.forEach((m, idx) => {
      const labelThreshold = labelStartAt + (idx / Math.max(N - 1, 1)) * labelSpan;
      if (progress >= labelThreshold) m.el.classList.add('label-visible');
      else m.el.classList.remove('label-visible');
    });

    // Rotas: drawing sincronizado via line-gradient + line-progress (per-feature).
    // Mesmo gradient aplica a TODAS as 9 rotas, cada uma cresce do próprio start (Sofia)
    // até a própria ponta. drawProgress 0 → 1 mapeado pra mesma janela do reveal dos pins.
    const drawProgress = Math.max(0, Math.min((progress - startAt) / span, 1));
    if (mapRef.current?.getLayer('route-line')) {
      try {
        let gradient;
        if (drawProgress >= 1) {
          // Tudo visível
          gradient = [
            'interpolate', ['linear'], ['line-progress'],
            0, 'rgba(250, 215, 140, 1)',
            1, 'rgba(250, 215, 140, 1)',
          ];
        } else if (drawProgress <= 0) {
          // Nada visível
          gradient = [
            'interpolate', ['linear'], ['line-progress'],
            0, 'rgba(250, 215, 140, 0)',
            1, 'rgba(250, 215, 140, 0)',
          ];
        } else {
          // Trim mask: visível [0, drawProgress], transparente [drawProgress, 1]
          // Stops precisam ser STRICTLY ascending — clampa pra evitar duplicates
          const e = Math.max(0.001, Math.min(drawProgress, 0.998));
          gradient = [
            'interpolate', ['linear'], ['line-progress'],
            0,           'rgba(250, 215, 140, 1)',
            e,           'rgba(250, 215, 140, 1)',
            e + 0.001,   'rgba(250, 215, 140, 0)',
            1,           'rgba(250, 215, 140, 0)',
          ];
        }
        mapRef.current.setPaintProperty('route-line', 'line-gradient', gradient);
        mapRef.current.setPaintProperty('route-line-halo', 'line-gradient', gradient);
      } catch (e) {}
    }
  });

  return (
    <Section ref={sectionRef}>
      <MarkerStyles />
      <MapContainer ref={containerRef} />
      <WarmOverlay />
    </Section>
  );
}
